import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, IconButton, Tooltip, Typography } from "@mui/material";
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CropFree as FitIcon,
  RotateRight as RotateIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { LuFileText } from "react-icons/lu";
import API_URL, {
  equipmentDrawingUrlPath,
  equipmentDrawingPreviewPath,
} from "../Config";
import axiosInstance from "../axiosinstance.js";
import "./DrawingPreviewModal.css";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

const getDrawingLabel = (file) =>
  file?.display_name || file?.filename || "Unnamed file";

const getDrawingKind = (file) => {
  const ext = (file?.file_type || file?.filename?.split(".").pop() || "")
    .toString()
    .toLowerCase();
  if (ext === "pdf") return "pdf";
  // TIFF can't render in a browser <img>; it's converted to PNG server-side.
  if (["tif", "tiff"].includes(ext)) return "tiff";
  if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(ext)) {
    return "image";
  }
  return "other";
};

// Branded loading indicator (Jecon favicon) shown while a drawing loads.
const DrawingLoader = ({ message }) => (
  <div className="drawing-preview-loading">
    <img
      src="/favicon.png"
      alt="Loading"
      className="drawing-preview-loading-logo"
    />
    <span className="drawing-preview-loading-text">{message}</span>
  </div>
);

/**
 * In-app drawing preview with next/previous navigation across a set, plus
 * zoom/pan for images (and server-converted TIFFs). PDF/JPG/PNG stream from a
 * short-lived signed S3 URL; TIFF is fetched as a server-converted PNG blob.
 *
 * Props:
 *   open          – bool
 *   files         – array of drawing objects (each with file_id)
 *   index         – index of the currently shown file
 *   onIndexChange – (nextIndex) => void
 *   onClose       – () => void
 */
const DrawingPreviewModal = ({
  open,
  files = [],
  index = 0,
  onIndexChange,
  onClose,
  canDelete = false,
  onDelete,
}) => {
  const file = files[index] || null;

  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(false); // source resolution in progress
  const [mediaLoading, setMediaLoading] = useState(false); // file bytes loading
  const [error, setError] = useState("");
  const [imageFailed, setImageFailed] = useState(false);

  // Zoom / pan / rotate (images & converted TIFFs only).
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const draggingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });

  const isImage = kind === "image" || kind === "tiff";
  const canPrev = index > 0;
  const canNext = index < files.length - 1;
  const goPrev = () => canPrev && onIndexChange?.(index - 1);
  const goNext = () => canNext && onIndexChange?.(index + 1);

  // Resolve the drawing source whenever the shown file changes.
  useEffect(() => {
    if (!open || !file?.file_id) return;

    let cancelled = false;
    let objectUrl = "";
    const fileKind = getDrawingKind(file);

    setLoading(true);
    setMediaLoading(false);
    setError("");
    setImageFailed(false);
    setName(getDrawingLabel(file));
    setKind(fileKind);
    setUrl("");
    // Reset the viewport for each new drawing.
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setRotation(0);

    (async () => {
      try {
        if (fileKind === "tiff") {
          const res = await axiosInstance.get(
            `${API_URL}${equipmentDrawingPreviewPath(file.file_id)}`,
            { responseType: "blob" },
          );
          objectUrl = URL.createObjectURL(res.data);
          if (!cancelled) {
            setUrl(objectUrl);
            setMediaLoading(true);
          }
        } else {
          const response = await axiosInstance.get(
            `${API_URL}${equipmentDrawingUrlPath(file.file_id)}`,
          );
          const signedUrl = response?.data?.url;
          if (!signedUrl) {
            throw new Error("No drawing URL returned");
          }
          if (!cancelled) {
            setUrl(signedUrl);
            setMediaLoading(fileKind === "pdf" || fileKind === "image");
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError("Unable to load preview. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, file]);

  // Lock background scroll while the preview is open.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Keyboard: arrows navigate the set, +/- zoom images.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
      else if (isImage && (e.key === "+" || e.key === "=")) {
        setZoom((z) => clamp(z + ZOOM_STEP, MIN_ZOOM, MAX_ZOOM));
      } else if (isImage && e.key === "-") {
        setZoom((z) => clamp(z - ZOOM_STEP, MIN_ZOOM, MAX_ZOOM));
      } else if (isImage && (e.key === "r" || e.key === "R")) {
        setRotation((r) => (r + 90) % 360);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, files.length, isImage]);

  const zoomIn = () => setZoom((z) => clamp(z + ZOOM_STEP, MIN_ZOOM, MAX_ZOOM));
  const zoomOut = () => setZoom((z) => clamp(z - ZOOM_STEP, MIN_ZOOM, MAX_ZOOM));
  const rotate = () => setRotation((r) => (r + 90) % 360);
  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setRotation(0);
  };

  const onPointerDown = (e) => {
    if (zoom <= 1) return;
    draggingRef.current = true;
    lastPointRef.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - lastPointRef.current.x;
    const dy = e.clientY - lastPointRef.current.y;
    lastPointRef.current = { x: e.clientX, y: e.clientY };
    setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
  };
  const endDrag = () => {
    draggingRef.current = false;
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: "drawing-preview-modal-paper" }}
      BackdropProps={{ sx: { overflow: "hidden" } }}
    >
      <div className="drawing-preview-header">
        <div className="drawing-preview-title-wrap">
          <LuFileText size={18} />
          <span className="drawing-preview-title-text">{name || "Preview"}</span>
          {files.length > 1 && (
            <span className="drawing-preview-counter">
              {index + 1} / {files.length}
            </span>
          )}
        </div>
        <div className="drawing-preview-actions">
          {isImage && !error && (
            <>
              <Tooltip title="Zoom out">
                <span>
                  <IconButton
                    size="small"
                    onClick={zoomOut}
                    disabled={zoom <= MIN_ZOOM}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <span className="drawing-preview-zoom-label">
                {Math.round(zoom * 100)}%
              </span>
              <Tooltip title="Zoom in">
                <span>
                  <IconButton
                    size="small"
                    onClick={zoomIn}
                    disabled={zoom >= MAX_ZOOM}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Rotate">
                <IconButton size="small" onClick={rotate}>
                  <RotateIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Fit">
                <IconButton size="small" onClick={resetView}>
                  <FitIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {canDelete && file && (
            <Tooltip title="Delete drawing">
              <IconButton
                size="small"
                onClick={() => onDelete?.(file)}
                aria-label="Delete drawing"
                sx={{ color: "#d92d20" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose} aria-label="Close preview">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <DialogContent dividers className="drawing-preview-content">
        {files.length > 1 && (
          <>
            <button
              type="button"
              className="drawing-preview-nav drawing-preview-nav-prev"
              onClick={goPrev}
              disabled={!canPrev}
              aria-label="Previous drawing"
            >
              <ChevronLeftIcon />
            </button>
            <button
              type="button"
              className="drawing-preview-nav drawing-preview-nav-next"
              onClick={goNext}
              disabled={!canNext}
              aria-label="Next drawing"
            >
              <ChevronRightIcon />
            </button>
          </>
        )}

        {loading ? (
          <DrawingLoader message="Preparing drawing…" />
        ) : error ? (
          <div className="drawing-preview-state">
            <Typography color="error">{error}</Typography>
          </div>
        ) : url ? (
          <>
            {mediaLoading && (
              <div className="drawing-preview-loading-overlay">
                <DrawingLoader message="Loading drawing… large files may take a moment." />
              </div>
            )}
            {kind === "pdf" ? (
              <iframe
                title={name || "PDF Preview"}
                src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
                className="drawing-preview-frame"
                onLoad={() => setMediaLoading(false)}
              />
            ) : isImage ? (
              <div
                className="drawing-preview-image-wrap"
                style={{ cursor: zoom > 1 ? "grab" : "default" }}
                onMouseDown={onPointerDown}
                onMouseMove={onPointerMove}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                onDoubleClick={resetView}
              >
                {imageFailed ? (
                  <div className="drawing-preview-state">
                    <Typography variant="body2" color="text.secondary">
                      Unable to load image preview. Please try downloading the
                      file.
                    </Typography>
                  </div>
                ) : (
                  <img
                    src={url}
                    alt={name || "Drawing preview"}
                    className="drawing-preview-image"
                    draggable={false}
                    style={{
                      opacity: mediaLoading ? 0 : 1,
                      transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                    }}
                    onLoad={() => setMediaLoading(false)}
                    onError={() => {
                      setMediaLoading(false);
                      setImageFailed(true);
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="drawing-preview-state">
                <Typography variant="body2" color="text.secondary">
                  Preview is not available for this file type.
                </Typography>
              </div>
            )}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default DrawingPreviewModal;
